import {
  AspectRatio,
  BorderStyle,
  Box,
  Color,
  GridDisplay,
  Image,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type {
  DashboardExperienceOrderNewAndLimitedResponse,
  DashboardExperienceOrderResponse,
  DashboardExperienceOrderTopPicksResponse,
  DashboardExperienceRecipeResponse,
} from '@library/api-dashboard-experience'
import React from 'react'
import { browserHistory } from 'react-router'

import { useOrderTracking } from '../../../hooks/useOrderTracking'
import EmptyPlateJPG from '../../../../../my-gousto-page/src/assets/emptyPlate.png'

const StyledImage = withCitrusTheme(Image, () => ({
  filter: 'drop-shadow(10px 17px 11px #00000021)',
  boxShadow:
    '1px 2px 5px 0px #0000001A, 4px 8px 9px 0px #00000017, 10px 17px 12px 0px #0000000D, 18px 31px 14px 0px #00000003, 28px 48px 16px 0px #00000000',
  borderRadius: '50%',
}))

const ClickableBox = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

const MAX_RECIPE_SLOTS = 5
const NON_CLICKABLE_ORDER_STATE_TITLES = new Set([
  "We're preparing your order",
  'Delivered',
  'Dispatched',
])

const getRecipeCollection = ({
  newAndLimited,
  recipe,
  topPicks,
}: {
  newAndLimited: DashboardExperienceOrderNewAndLimitedResponse | null
  recipe: DashboardExperienceRecipeResponse | null
  topPicks: DashboardExperienceOrderTopPicksResponse | null
}): 'top picks for you' | 'new and limited edition' | undefined => {
  if (!recipe) {
    return undefined
  }

  if (topPicks?.recipes.some(({ id }) => id === recipe.id)) {
    return 'top picks for you'
  }

  if (newAndLimited?.recipes.some(({ id }) => id === recipe.id)) {
    return 'new and limited edition'
  }

  return undefined
}

const RecipePlaceholder = () => (
  <AspectRatio ratio={1 / 1}>
    <Box
      borderStyle={BorderStyle.Dashed}
      borderColor={Color.ColdGrey_200}
      borderWidth={0.5}
      width="100%"
      data-testid="empty-recipe-image"
      paddingBottom={[1, 3]}
      paddingH={[1, 3]}
      paddingRight={[1, 3]}
      paddingV={[1, 3]}
    >
      <StyledImage src={EmptyPlateJPG} alt="Recipe placeholder" />
    </Box>
  </AspectRatio>
)

const RecipeTile = ({ src }: { src: string }) => (
  <AspectRatio ratio={1 / 1}>
    <Image src={src} alt="Recipe Image" />
  </AspectRatio>
)

type OrderTileRecipesProps = {
  newAndLimited: DashboardExperienceOrderNewAndLimitedResponse | null
  orderId: string
  orderStateTitle: DashboardExperienceOrderResponse['state_title']
  recipes: DashboardExperienceOrderResponse['recipes']
  topPicks: DashboardExperienceOrderTopPicksResponse | null
}

export const OrderTileRecipes = ({
  newAndLimited,
  orderId,
  orderStateTitle,
  recipes,
  topPicks,
}: OrderTileRecipesProps) => {
  const { trackRecommendedRecipeClick } = useOrderTracking()
  const isClickable = !NON_CLICKABLE_ORDER_STATE_TITLES.has(orderStateTitle)
  const recipeSlots = Array.from({ length: MAX_RECIPE_SLOTS }, (_, index) => recipes[index] ?? null)
  const handleClick = (recipe: DashboardExperienceRecipeResponse | null) => {
    if (!isClickable) {
      return
    }

    trackRecommendedRecipeClick({
      collection: getRecipeCollection({
        newAndLimited,
        recipe,
        topPicks,
      }),
      orderId,
    })
    browserHistory.push(`/menu/${orderId}`)
  }

  return (
    <Box
      width={['100%', '100%', '75%']}
      display={GridDisplay.Grid}
      gridTemplateColumns={`repeat(${MAX_RECIPE_SLOTS}, 1fr)`}
      gap={[2, 4]}
    >
      {recipeSlots.map((recipe, index) => {
        const RecipeSlot = isClickable ? ClickableBox : Box

        return (
          <RecipeSlot
            key={`${orderId}-recipe-slot-${index}`}
            width="100%"
            onClick={isClickable ? () => handleClick(recipe) : undefined}
            onKeyDown={
              isClickable
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleClick(recipe)
                    }
                  }
                : undefined
            }
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
          >
            {recipe?.image_url ? <RecipeTile src={recipe.image_url} /> : <RecipePlaceholder />}
          </RecipeSlot>
        )
      })}
    </Box>
  )
}
